import { FormattedMessage, useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { DEFAULT_VALUES } from "@/libraries/web-framework";
import {
    EnhancedBannerAlert,
    EnhancedBannerProps
} from "@/patterns/adg4/enhanced-banner-alert/enhanced-banner-alert";
import { GWP8238AcquisitionBanner } from "@/patterns/adg4/enhanced-banner-alert/gwp-8238-acquisition-banner";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

const Team25EuPhase5DetailsDesktop = () => (
    <FormattedMessage
        id="HomePage.banner-alert.team-25-eu-phase-5.event-details"
        defaultMessage="<strong>Catch up on what you missed</strong> See what’s new with AI-powered technology & other product updates "
        values={DEFAULT_VALUES}
    />
);

const Team25EuPhase5DetailsMobile = () => (
    <FormattedMessage
        id="HomePage.banner-alert.team-25-eu-phase-5.event-details-mobile"
        defaultMessage="<strong>Watch on-demand sessions</strong> Catch up on what you missed!"
        values={DEFAULT_VALUES}
    />
);

const SHARED_TEAM_25_PRESETS = {
    imageProps: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5j3bp77j147xlj1sgq43ed5y57nkkg66/team-25-eu-right.svg",
        alt: "Team25 EU",
        width: 136
    },
    imageMobileProps: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/flgpq367h183c5d5svw6b52j5hh74y76/team-25-eu-centered.svg",
        alt: "Team25 EU",
        width: 136
    },
    productKey: "home" as const,
    backgroundPreset: "gwp-5090-banners" as const
};

export const HomePageBannerAlert = () => {
    const intl = useIntl();

    if (checkFeatureFlagGate(featureFlags.gwp8510AustinGrandPrixBanner)) {
        const austinGrandPrixLinkText = intl.formatMessage({
            id: "HomePage.banner-alert.austin-grand-prix.link-text",
            defaultMessage: "Learn more"
        });

        const austinGrandPrixConfig: EnhancedBannerProps = {
            identifier: "HomePageTemplateV2.banner.austin-grand-prix",
            text: (
                <>
                    <FormattedMessage
                        id="HomePage.banner-alert.austin-grand-prix.event-details"
                        defaultMessage="<strong>Transformation takes a team.</strong> Atlassian and Williams: forward together."
                        values={DEFAULT_VALUES}
                    />
                    <span data-testid="austin-grand-prix" />
                </>
            ),
            mobileText: (
                <FormattedMessage
                    id="HomePage.banner-alert.austin-grand-prix.event-details-mobile"
                    defaultMessage="Atlassian and Williams: forward together."
                    values={DEFAULT_VALUES}
                />
            ),
            linkText: austinGrandPrixLinkText,
            linkUrl:
                "https://www.atlassian.com/enterprise/atlassian-williams-racing",
            productKey: "home" as const,
            backgroundPreset: "gwp-8510-banners" as const,
            imageProps: {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/s1iqaf16aeapp4x21qdx5s1cngfs8px2.webp",
                alt: "Atlassian Williams Racing",
                width: 106
            },
            imageMobileProps: {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/t6n8a5dh5o40o5118a74ml7r025k76l7.webp",
                alt: "Atlassian Williams Racing",
                width: 240
            },
            dataEventProps: {
                "data-product-key": "home",
                "data-event-component": "linkButton",
                "data-event-container": "announcementBanner",
                "data-event": "clicked",
                "data-label": austinGrandPrixLinkText,
                "data-label-english": "Learn more"
            }
        };

        return (
            <UFOSegment name="homepage-v2-bannerAlert-austin-gp">
                <EnhancedBannerAlert {...austinGrandPrixConfig} />
            </UFOSegment>
        );
    }

    if (checkFeatureFlagGate(featureFlags.gwp8404Team25EuBannerPhase5)) {
        const linkText = intl.formatMessage({
            id: "HomePage.banner-alert.team-25-eu-phase-5.link-text",
            defaultMessage: "Watch on demand"
        });

        const team25EuPhase5Config: EnhancedBannerProps = {
            ...SHARED_TEAM_25_PRESETS,
            identifier: "HomePageTemplateV2.banner.team-25-eu-phase-5",
            text: (
                <>
                    <Team25EuPhase5DetailsDesktop />
                    <span data-testid="team-25-eu-phase-5" />
                </>
            ),
            mobileText: <Team25EuPhase5DetailsMobile />,
            linkText: linkText,
            linkUrl:
                "https://events.atlassian.com/teameurope?utm_medium=event-web-referral&utm_source=cross-promo&event_context=701qb00000pbqkqyar&utm_campaign=K:event|H:in-person|N:team_europe_25|P:agnostic|O:event-marketing|F:pre-event|C:event-home&utm_content=on_demand_banner&event_id=243301",
            dataEventProps: {
                "data-product-key": "home",
                "data-event-component": "linkButton",
                "data-event-container": "announcementBanner",
                "data-event": "clicked",
                "data-label": linkText,
                "data-label-english": "Watch on demand"
            }
        };

        return (
            <UFOSegment name="homepage-v2-bannerAlert">
                <EnhancedBannerAlert {...team25EuPhase5Config} />
            </UFOSegment>
        );
    }

    // DX acquisition banner
    return (
        <GWP8238AcquisitionBanner
            productKey="home"
            identifier="HomePageTemplateV2.banner.gwp-8238-announcement"
            ufoSegmentName="homepage-v2-bannerAlert-gwp8238"
            testId="gwp-8238-announcement"
            linkTextId="HomePage.banner-alert.gwp-8238-announcement.link-text"
            linkTextDefault="Learn more"
        />
    );
};
